import React, { useEffect, useRef, useState } from "react";
import { Send, Bot, User as UserIcon, FileText, Loader2 } from "lucide-react";
import { v2Api } from "@/lib/v2Api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

/** Full-page version of the floating chat widget. Useful when the user
 * wants room to read citations comfortably. */
export default function ChatPage() {
  const [projects, setProjects] = useState([]);
  const [scope, setScope] = useState("all"); // all | project | rfp
  const [scopeProjectId, setScopeProjectId] = useState("");
  const [scopeRfpId, setScopeRfpId] = useState("");
  const [rfps, setRfps] = useState([]);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { v2Api.projects.list().then(setProjects).catch(() => {}); }, []);
  useEffect(() => {
    if (scope === "rfp" && scopeProjectId) {
      v2Api.rfp.listForProject(scopeProjectId).then(setRfps).catch(() => setRfps([]));
    }
  }, [scope, scopeProjectId]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (e) => {
    if (e) e.preventDefault();
    const q = draft.trim();
    if (!q) return;
    setDraft("");
    setMessages((m) => [...m, { role: "user", text: q, ts: Date.now() }]);
    setBusy(true);
    try {
      const payload = { query: q, k: 8 };
      if (scope === "project" && scopeProjectId) payload.project_id = scopeProjectId;
      if (scope === "rfp" && scopeRfpId) payload.rfp_id = scopeRfpId;
      const res = await v2Api.chat(payload);
      setMessages((m) => [...m, {
        role: "assistant",
        text: res.answer || "(empty)",
        citations: res.citations || [],
        model: res.model,
      }]);
    } catch (err) {
      setMessages((m) => [...m, {
        role: "assistant",
        text: "(error: " + (err?.response?.data?.detail ?? err.message) + ")",
      }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto h-[calc(100vh-3rem)] flex flex-col gap-3">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Bot className="w-5 h-5 text-blue-600" /> Ask the RFP corpus
        </h1>
        <p className="text-sm text-slate-500">
          Grounded answers with page-level citations from your indexed RFPs.
          Set scope below to narrow to one project or one document.
        </p>
      </div>

      <Card>
        <CardContent className="py-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-500 mr-1">Scope:</span>
          {["all", "project", "rfp"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setScope(s)}
              className={`px-2 py-0.5 rounded-full border ${scope === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
            >
              {s === "all" ? "All RFPs" : s === "project" ? "One project" : "One RFP"}
            </button>
          ))}
          {scope !== "all" && (
            <select
              value={scopeProjectId}
              onChange={(e) => { setScopeProjectId(e.target.value); setScopeRfpId(""); }}
              className="text-xs border border-slate-200 rounded px-1 py-0.5 bg-white max-w-xs"
            >
              <option value="">Pick project…</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </select>
          )}
          {scope === "rfp" && rfps.length > 0 && (
            <select
              value={scopeRfpId}
              onChange={(e) => setScopeRfpId(e.target.value)}
              className="text-xs border border-slate-200 rounded px-1 py-0.5 bg-white max-w-xs"
            >
              <option value="">Pick RFP…</option>
              {rfps.map((r) => <option key={r.id} value={r.id}>{r.title?.slice(0, 60)}</option>)}
            </select>
          )}
        </CardContent>
      </Card>

      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-slate-50 rounded-md border p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-sm text-slate-400 py-10">
            Ask anything about your RFPs.<br />
            <span className="text-xs">"Which RFPs need NABL accreditation?" · "Summarise the EMD clauses" · "What's the deadline for JJM?"</span>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && (
              <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                <Bot className="w-4 h-4" />
              </div>
            )}
            <div className={`max-w-[80%] ${m.role === "user" ? "bg-blue-600 text-white" : "bg-white border text-slate-800"} rounded-lg px-4 py-3 text-sm`}>
              <div className="whitespace-pre-wrap">{m.text}</div>
              {m.citations && m.citations.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200 space-y-1.5">
                  {m.citations.map((c, j) => (
                    <div key={j} className="text-xs text-slate-600 flex items-start gap-1.5">
                      <span className="font-mono text-slate-400 shrink-0">[{j + 1}]</span>
                      <FileText className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-slate-700 font-medium truncate">
                          {c.rfp_id ? (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                v2Api.rfp.openFileInNewTab(c.rfp_id, c.page).catch(() => {});
                              }}
                              rel="noreferrer"
                              className="text-blue-600 hover:underline"
                              title={`Open ${c.rfp_title || "RFP"} at page ${c.page}`}
                            >
                              page {c.page} · {c.rfp_title || "RFP"} ↗
                            </a>
                          ) : (
                            <>page {c.page} · {c.rfp_title || "RFP"}</>
                          )}
                        </div>
                        <div className="text-slate-500 line-clamp-3">{c.snippet}</div>
                      </div>
                    </div>
                  ))}
                  {m.model && <p className="text-[10px] text-slate-300 italic">model: {m.model}</p>}
                </div>
              )}
            </div>
            {m.role === "user" && (
              <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center shrink-0">
                <UserIcon className="w-4 h-4" />
              </div>
            )}
          </div>
        ))}
        {busy && (
          <div className="flex gap-2 justify-start">
            <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center"><Bot className="w-4 h-4 text-blue-700" /></div>
            <div className="bg-white border rounded-lg px-3 py-2 text-sm flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin text-blue-600" />
              <span className="text-slate-500 text-xs">Searching index + grounding answer…</span>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={send} className="flex items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask about the RFPs in scope…"
          disabled={busy}
          autoFocus
        />
        <Button type="submit" disabled={busy || !draft.trim()}>
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}
